import { useEffect, useState, useCallback } from "react";
import { Plus, CalendarClock, CheckCircle2, Trash2, Pencil, Lightbulb, Users, MailPlus, Copy, Loader2 } from "lucide-react";
import { api, useAuth, formatApiError, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import StatutBadge from "../components/StatutBadge";
import SessionDialog from "../components/SessionDialog";
import { toast } from "sonner";

export default function Sessions() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "sessions", "modifier");
  const [sessions, setSessions] = useState([]);
  const [convo, setConvo] = useState({ open: false, loading: false, texte: "", date: "" });

  const genConvo = async (s) => {
    setConvo({ open: true, loading: true, texte: "", date: s.date });
    try {
      const { data } = await api.post(`/sessions/${s.id}/convocation`);
      setConvo({ open: true, loading: false, texte: data.texte, date: s.date });
    } catch (e) {
      toast.error(formatApiError(e));
      setConvo({ open: false, loading: false, texte: "", date: "" });
    }
  };
  const [suggestions, setSuggestions] = useState([]);
  const [regles, setRegles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preset, setPreset] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/sessions"), api.get("/sessions/suggestions"), api.get("/sessions/suggestions?type=incendie")])
      .then(([s, sug, sugFi]) => {
        setSessions(s.data.sessions);
        setRegles(s.data.regles_inrs);
        setSuggestions([...sug.data.suggestions, ...sugFi.data.suggestions]);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const complete = async (s) => {
    const estIncendie = s.type === "incendie";
    if (!window.confirm(`Marquer la session du ${s.date} comme réalisée ? ${estIncendie ? `Les formations incendie des ${s.nb_participants} participant(s) seront renouvelées.` : `Les certificats des ${s.nb_participants} participant(s) seront renouvelés pour 24 mois.`}`)) return;
    try {
      await api.post(`/sessions/${s.id}/complete`);
      toast.success(estIncendie ? "Session réalisée — formations incendie renouvelées" : "Session réalisée — certificats renouvelés (24 mois)");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer la session du ${s.date} ?`)) return;
    try {
      await api.delete(`/sessions/${s.id}`);
      toast.success("Session supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openSuggestion = (sug) => {
    setPreset({ date: sug.date_proposee, participant_ids: sug.participants.map((p) => p.id), type: sug.type || "mac_sst" });
    setEditing(null);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-8" data-testid="sessions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-accent mb-1">Maintien des Acquis de Compétences</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Sessions MAC</h1>
          {regles && (
            <p className="text-sm text-muted-foreground mt-1" data-testid="inrs-rules">
              Référentiel INRS : {regles.duree_mac_heures}h par session · {regles.min_participants} à {regles.max_participants} participants · validité {regles.validite_mois} mois
            </p>
          )}
        </div>
        {!readOnly && (
          <Button onClick={() => { setPreset(null); setEditing(null); setDialogOpen(true); }} data-testid="create-session-button">
            <Plus className="h-4 w-4 mr-2" /> Planifier une session
          </Button>
        )}
      </div>

      {suggestions.length > 0 && (
        <div className="rounded-xl border border-amber-800/60 bg-amber-950/20 p-5" data-testid="suggestions-panel">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb className="h-4 w-4 text-amber-400" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Suggestions de dates</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-1">Groupes formés automatiquement selon les urgences d'expiration (règle INRS : min. 4 participants pour lancer une session)</p>
          <p className="text-xs text-amber-400/80 mb-4">Contraintes magasin : 93 MAI = 1 personne à la fois (+ le responsable J.-P. Douyssard) · 92 SRV = 2 personnes à la fois (+ le responsable E. Ribleau) · les certificats dépassés ne sont pas proposés (formation initiale SST à refaire)</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {suggestions.map((sug, i) => (
              <div key={`${sug.type || "mac_sst"}-${sug.date_proposee}`} className="rounded-lg border border-border bg-card p-4" data-testid={`suggestion-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold font-mono text-accent">{sug.date_proposee}</p>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${sug.type === "incendie" ? "bg-sky-950 text-sky-400 border-sky-700" : "bg-emerald-950 text-emerald-400 border-emerald-700"}`}>
                        {sug.type === "incendie" ? "INCENDIE" : "SST"}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">1ère expiration : {sug.expiration_min}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${sug.quota_atteint ? "bg-emerald-950 text-emerald-400 border-emerald-700" : "bg-red-950 text-red-400 border-red-700"}`}>
                    {sug.nb_participants}/{regles?.max_participants} {sug.quota_atteint ? "· quota OK" : "· quota insuffisant"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {sug.participants.map((p) => (
                    <span key={p.id} className="text-xs bg-secondary rounded-full px-2 py-0.5">{p.prenom} {p.nom} <span className="text-muted-foreground">({p.service})</span></span>
                  ))}
                </div>
                {!readOnly && (
                  <Button size="sm" variant="outline" onClick={() => openSuggestion(sug)} data-testid={`create-suggested-session-${i}`}>
                    <CalendarClock className="h-4 w-4 mr-2" /> Planifier cette session
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4">
        <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Sessions planifiées & réalisées</h2>
        {loading && <p className="text-sm text-muted-foreground">Chargement...</p>}
        {!loading && sessions.length === 0 && (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="sessions-empty">
            Aucune session planifiée. Utilisez les suggestions ci-dessus ou créez une session manuellement.
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sessions.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid={`session-card-${s.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-lg font-bold font-mono">{s.date}</p>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${s.type === "incendie" ? "bg-sky-950 text-sky-400 border-sky-700" : "bg-emerald-950 text-emerald-400 border-emerald-700"}`} data-testid={`session-type-${s.id}`}>
                      {s.type === "incendie" ? "FORMATION INCENDIE" : "MAC SST"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{s.type === "incendie" ? "Recyclage formation incendie" : `MAC · ${s.duree_heures}h (1 journée)`}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${s.statut === "realisee" ? "bg-emerald-950 text-emerald-400 border-emerald-700" : "bg-blue-950 text-blue-400 border-blue-700"}`}>
                  {s.statut === "realisee" ? "Réalisée" : "Planifiée"}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">{s.nb_participants}/{s.max_participants} participants</span>
                {s.nb_participants < s.min_participants && s.statut === "planifiee" && (
                  <span className="text-xs text-red-400">(min. {s.min_participants} requis)</span>
                )}
              </div>
              <Progress value={(s.nb_participants / s.max_participants) * 100} className="h-2 mb-3" />
              <div className="flex flex-wrap gap-1.5 mb-4">
                {s.participants.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1.5 text-xs bg-secondary rounded-full px-2 py-0.5">
                    {p.prenom} {p.nom}
                    <StatutBadge statut={p.statut} joursRestants={p.jours_restants} showDays={false} />
                  </span>
                ))}
              </div>
              {s.notes && <p className="text-xs text-muted-foreground mb-3">{s.notes}</p>}
              {!readOnly && (
                <div className="flex gap-2 flex-wrap">
                  {s.statut === "planifiee" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => complete(s)} data-testid={`complete-session-${s.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marquer réalisée
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => genConvo(s)} data-testid={`convocation-session-${s.id}`}>
                        <MailPlus className="h-4 w-4 mr-1.5" /> Convocation
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(s); setPreset(null); setDialogOpen(true); }} data-testid={`edit-session-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="hover:text-red-400" onClick={() => remove(s)} data-testid={`delete-session-${s.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <SessionDialog open={dialogOpen} onOpenChange={setDialogOpen} session={editing} preset={preset} onSaved={load} />

      <Dialog open={convo.open} onOpenChange={(o) => setConvo({ ...convo, open: o })}>
        <DialogContent className="max-w-2xl" data-testid="convocation-dialog">
          <DialogHeader>
            <DialogTitle>Convocation — session du {convo.date}</DialogTitle>
            <DialogDescription>Générée par Claude Sonnet 5. Copiez-la dans votre messagerie pour l'envoyer aux participants.</DialogDescription>
          </DialogHeader>
          {convo.loading ? (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Génération en cours (10-20 s)...
            </p>
          ) : (
            <>
              <div className="rounded-lg border border-border bg-background p-4 max-h-96 overflow-y-auto">
                <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid="convocation-text">{convo.texte}</p>
              </div>
              <DialogFooter>
                <Button onClick={() => { navigator.clipboard.writeText(convo.texte); toast.success("Convocation copiée"); }} data-testid="convocation-copy-button">
                  <Copy className="h-4 w-4 mr-2" /> Copier le texte
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
